import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * A form's preview (`Action.preview`): while the person fills the form in, the server is asked what
 * sending it would come to, and the answer is drawn in the form before they press its button — the
 * balance now, the days asked, the balance after.
 *
 * The bulk preview's sibling (`bulk.ts`, docs/list-standard.md §6) and built from its pieces: the
 * same path (`previewPathFor`, the action's own with `/preview` after it), the same reading of an
 * answer (`verdictOf`: `{applies: false, reason}` or a refusal is "does not apply", with its reason),
 * the same columns. Two things differ, because a form is filled in while the question is asked:
 *
 * - **When.** Only once the form is ready (`previewReady`: every required field, nothing the form's
 *   own rules refuse), again whenever the values change, settled first (`FORM_PREVIEW_DELAY`), and
 *   only the latest question's answer is ever drawn — an answer to older values is dropped.
 * - **What it holds back.** Nothing. The submit stays the person's: the server judges again when the
 *   form is sent, and a preview is what it would say now, not a promise.
 *
 * The asking is a plain object (`formPreviewAsker`) so the timing and the staleness are tested
 * without a browser; `useFormPreview` is its React side, and `FormPreview` draws the four states.
 */
import { useEffect, useRef, useState } from 'react';
import { adviceOfAnswer } from "./manifest.js";
import { verdictOf } from "./bulk.js";
import { formatCell } from "./render.js";
import { resolveLabel } from "./derive.js";
import { Notice, cx } from "./ui.js";
/** How long the values must stand still before the server is asked about them. */
export const FORM_PREVIEW_DELAY = 400;
/**
 * One answer read as a state. A 2xx draws its facts unless it says `applies: false`; a refusal is
 * the server's reason (the bulk preview reads both the same way, `verdictOf`); anything else is a
 * preview that could not be had — said quietly, because the form still goes.
 */
export function formPreviewOf(outcome, i18n) {
    if (outcome.state !== 'ok' && outcome.state !== 'rejected')
        return { state: 'failed' };
    const verdict = verdictOf('form', outcome, i18n);
    if (verdict.applies)
        return { state: 'answer', data: verdict.data ?? {} };
    // An answer that says it does not apply and not why is said in the form's words, not a row's.
    const said = verdict.data && typeof verdict.data['reason'] === 'string' && verdict.data['reason'];
    const reason = outcome.state === 'rejected' ? verdict.reason ?? '' : said || i18n.t('form.preview.notApplies');
    return { state: 'refused', reason: reason || i18n.t('form.preview.notApplies'), ...(verdict.data ? { data: verdict.data } : {}) };
}
const REAL_TIMERS = {
    set: (fn, ms) => setTimeout(fn, ms),
    clear: (handle) => clearTimeout(handle),
};
/**
 * Asks the server about a form, one question at a time. `update` is told the question the form
 * holds now — null while it is not ready — on every change; the same question twice asks nothing.
 * A new question cancels the wait for the last one, and an answer that arrives after a newer
 * question was put is dropped, whichever request the network happens to finish first.
 */
export function formPreviewAsker(ask, onState, i18n, delay = FORM_PREVIEW_DELAY, timers = REAL_TIMERS) {
    let last;
    let timer = null;
    let asked = 0;
    const cancel = () => { if (timer !== null) {
        timers.clear(timer);
        timer = null;
    } };
    return {
        update(question) {
            const key = question ? JSON.stringify([question.path, question.body]) : '';
            if (key === last)
                return;
            last = key;
            cancel();
            const mine = ++asked;
            if (!question) {
                onState({ state: 'waiting' });
                return;
            }
            onState({ state: 'asking' });
            timer = timers.set(() => {
                timer = null;
                void ask(question.path, question.body).then((outcome) => { if (mine === asked)
                    onState(formPreviewOf(outcome, i18n)); }, () => { if (mine === asked)
                    onState({ state: 'failed' }); });
            }, delay);
        },
        dispose() {
            cancel();
            asked++;
        },
    };
}
/**
 * The preview of the form on screen. `question` is what the form would send now, or null while it
 * is not ready; `ask` sends a preview request (`POST`, no idempotency key — nothing is written).
 * Null `spec`: the form declares no preview, nothing is asked, and the state stays `waiting`.
 */
export function useFormPreview(spec, question, ask, i18n) {
    const [state, setState] = useState({ state: 'waiting' });
    const askRef = useRef(ask);
    askRef.current = ask;
    const asker = useRef(null);
    useEffect(() => {
        if (!spec)
            return;
        const a = formPreviewAsker((p, b) => askRef.current(p, b), setState, i18n);
        asker.current = a;
        return () => { a.dispose(); asker.current = null; };
    }, [Boolean(spec)]);
    const key = question ? JSON.stringify([question.path, question.body]) : '';
    useEffect(() => { asker.current?.update(question); }, [key, Boolean(spec)]);
    return state;
}
/**
 * What the form would come to, drawn after its fields (a dialog) or beside them (a page's `aside`):
 * a muted line while it waits for the required fields or for the answer, the answer's facts, an
 * amber notice with the server's reason when it says the form would not go through, and a muted
 * «preview unavailable» when no answer could be had. It never disables anything.
 */
export function FormPreview({ spec, state, i18n, aside = false }) {
    const title = spec.label || spec.label_key ? resolveLabel(spec.label, spec.label_key, i18n) : i18n.t('form.preview.title');
    const line = (key) => _jsx("p", { className: "text-[13px] text-muted-foreground", dir: "auto", "data-form-preview-line": state.state, children: i18n.t(key) });
    const facts = (data) => (_jsx("dl", { "data-form-preview-facts": "", className: cx('grid gap-x-4 gap-y-2', aside ? 'grid-cols-1' : spec.columns.length > 2 ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-2'), children: spec.columns.map((c) => (_jsxs("div", { className: "min-w-0", "data-form-preview-fact": c.key, children: [_jsx("dt", { className: "text-[12px] text-muted-foreground", dir: "auto", children: resolveLabel(c.label, c.label_key, i18n) }), _jsx("dd", { className: "truncate text-[14px] font-semibold tabular-nums", dir: "auto", children: data[c.key] === undefined || data[c.key] === null ? '—' : formatCell(data[c.key], c, i18n) || '—' })] }, c.key))) }));
    // A warning the facts cannot carry — a full session, a clash with a shift — said under them, never a refusal.
    const advice = (data) => {
        const lines = adviceOfAnswer(data);
        if (!lines.length)
            return null;
        return (_jsx("div", { "data-form-preview-advice": "", className: "grid gap-2", children: lines.map((l, i) => {
                const said = l.key ? i18n.t(l.key, l.vars) : '';
                return _jsx(Notice, { tone: l.tone, "data-advice": l.key ?? '', children: l.key && said !== l.key ? said : l.message }, i);
            }) }));
    };
    return (_jsxs("section", { "data-form-preview": state.state, "aria-live": "polite", className: cx('grid gap-2 rounded-lg border bg-muted/50 px-4 py-3', aside && 'bg-card p-5 shadow-card'), children: [_jsx("h3", { className: "text-[12.5px] font-semibold text-muted-foreground", dir: "auto", children: title }), state.state === 'waiting' ? line('form.preview.waiting')
                : state.state === 'asking' ? line('form.preview.asking')
                    : state.state === 'failed' ? line('form.preview.unavailable')
                        : state.state === 'refused' ? (_jsx(Notice, { tone: "amber", "data-form-preview-refused": "", children: state.reason })) : facts(state.data), state.state === 'answer' ? advice(state.data) : null] }));
}
//# sourceMappingURL=form-preview.js.map